// src/pages/Admin/Users/UserDetailModal.tsx
import React from 'react';
import { 
  X, 
  Mail, 
  Phone, 
  Calendar, 
  Shield, 
  MapPin, 
  CreditCard,
  CheckCircle,
  XCircle,
  Clock,
  UserCog
} from 'lucide-react';
import type { User } from '../../../types';

interface UserDetailModalProps {
  user: User;
  onClose: () => void;
  onRoleEdit?: () => void;
}

const UserDetailModal: React.FC<UserDetailModalProps> = ({ 
  user, 
  onClose, 
  onRoleEdit 
}) => {
  const getRoleBadgeColor = (role: string) => {
    const colors = {
      admin: 'bg-red-100 text-red-800',
      manager: 'bg-purple-100 text-purple-800',
      staff: 'bg-blue-100 text-blue-800',
      driver: 'bg-green-100 text-green-800',
      warehouse: 'bg-orange-100 text-orange-800',
      customer: 'bg-gray-100 text-gray-800'
    };
    return colors[role as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center">
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                <span className="text-lg font-medium text-blue-600">
                  {user.first_name?.[0]}{user.last_name?.[0]}
                </span>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  {user.full_name || `${user.first_name} ${user.last_name}`}
                </h3>
                <p className="text-sm text-gray-500">User ID: {user.id}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X size={20} />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
            <div className="space-y-6">
              {/* Contact Information */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                  <Mail size={16} className="mr-2 text-gray-500" />
                  Contact Information
                </h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Email:</span>
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900 mr-2">{user.email}</span>
                      {user.email_verified ? (
                        <CheckCircle size={16} className="text-green-500" />
                      ) : (
                        <XCircle size={16} className="text-red-500" />
                      )}
                    </div>
                  </div>
                  {user.phone && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Phone:</span>
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-900 mr-2">{user.phone}</span>
                        {user.phone_verified ? (
                          <CheckCircle size={16} className="text-green-500" />
                        ) : (
                          <XCircle size={16} className="text-red-500" />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Roles */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                  <Shield size={16} className="mr-2 text-gray-500" />
                  Roles & Permissions
                </h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex flex-wrap gap-2 mb-3">
                    {user.roles?.map((role) => (
                      <span
                        key={role}
                        className={`px-3 py-1.5 text-sm font-medium rounded-full ${getRoleBadgeColor(role)}`}
                      >
                        {role}
                      </span>
                    ))}
                  </div>
                  {onRoleEdit && (
                    <button
                      onClick={onRoleEdit}
                      className="flex items-center text-sm text-blue-600 hover:text-blue-700"
                    >
                      <UserCog size={16} className="mr-1" />
                      Manage Roles
                    </button>
                  )}
                </div>
              </div>

              {/* ID Verification */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                  <CreditCard size={16} className="mr-2 text-gray-500" />
                  ID Verification
                </h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Status:</span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      user.is_id_verified ? 'bg-green-100 text-green-800' :
                      user.id_verification_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      user.id_verification_status === 'rejected' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {user.id_verification_status || (user.is_id_verified ? 'Verified' : 'Not Uploaded')}
                    </span>
                  </div>
                  {user.id_verified_at && (
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-sm text-gray-600">Verified at:</span>
                      <span className="text-sm text-gray-900">{formatDate(user.id_verified_at)}</span>
                    </div>
                  )}
                  {user.id_uploaded_at && (
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-sm text-gray-600">Uploaded at:</span>
                      <span className="text-sm text-gray-900">{formatDate(user.id_uploaded_at)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Account Information */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                  <Calendar size={16} className="mr-2 text-gray-500" />
                  Account Information
                </h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Status:</span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Can place orders:</span>
                    <span className={`text-sm font-medium ${user.can_place_orders ? 'text-green-600' : 'text-red-600'}`}>
                      {user.can_place_orders ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Joined:</span>
                    <span className="text-sm text-gray-900">{formatDate(user.created_at)}</span>
                  </div>
                  {user.last_login && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Last login:</span>
                      <span className="text-sm text-gray-900">{formatDate(user.last_login)}</span>
                    </div>
                  )}
                  {user.password_changed_at && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Password last changed:</span>
                      <span className="text-sm text-gray-900">{formatDate(user.password_changed_at)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetailModal;